import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import confetti from "canvas-confetti";
import { Heart, Star, Sparkles } from "lucide-react";
import "./App.css";

const App = () => {
  const [clickedButton, setClickedButton] = useState(null);

  const coupleImages = [
    "https://customer-assets.emergentagent.com/job_heart-to-heart-21/artifacts/fooomhhu_WhatsApp%20Image%202026-01-30%20at%2013.11.10%20%281%29.jpeg",
    "https://customer-assets.emergentagent.com/job_heart-to-heart-21/artifacts/opsctfez_WhatsApp%20Image%202026-01-30%20at%2013.10.12.jpeg",
    "https://customer-assets.emergentagent.com/job_heart-to-heart-21/artifacts/33ma5uug_WhatsApp%20Image%202026-01-30%20at%2013.11.09.jpeg"
  ];

  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const handleButtonClick = (buttonId) => {
    setClickedButton(buttonId);
    setTimeout(() => setClickedButton(null), 3000);
  };

  const triggerConfetti = () => {
    confetti({
      particleCount: 150,
      spread: 80,
      origin: { y: 0.6 },
      colors: ['#FFB6C1', '#FFD1DC', '#E6E6FA', '#FFC0CB', '#FF69B4']
    });
  };

  const Section = ({ children, className }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: false, amount: 0.3 });

    return (
      <motion.div
        ref={ref}
        className={className}
        initial={{ opacity: 0, y: 80 }}
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 80 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    );
  };

  return (
    <div className="valentine-scroll" ref={containerRef}>
      {/* Progress indicator */}
      <motion.div 
        className="scroll-progress"
        style={{ scaleX: scrollYProgress }}
      />

      {/* Subtle floating bow decorations */}
      <div className="bow-decorations">
        <motion.div className="bow bow-1" animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }} transition={{ duration: 3, repeat: Infinity }}>
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <path d="M15 20c-3 0-7-2-7-7s4-7 7-7 4 4 4 7-1 7-4 7zm10 0c3 0 7-2 7-7s-4-7-7-7-4 4-4 7 1 7 4 7z" fill="#FFB6C1" opacity="0.4"/>
            <circle cx="20" cy="20" r="3" fill="#FF69B4" opacity="0.5"/>
          </svg>
        </motion.div>
        <motion.div className="bow bow-2" animate={{ y: [0, -15, 0], rotate: [0, -5, 0] }} transition={{ duration: 4, repeat: Infinity, delay: 1 }}>
          <svg width="35" height="35" viewBox="0 0 40 40" fill="none">
            <path d="M15 20c-3 0-7-2-7-7s4-7 7-7 4 4 4 7-1 7-4 7zm10 0c3 0 7-2 7-7s-4-7-7-7-4 4-4 7 1 7 4 7z" fill="#E6E6FA" opacity="0.4"/>
            <circle cx="20" cy="20" r="3" fill="#9370DB" opacity="0.5"/>
          </svg>
        </motion.div>
        <motion.div className="bow bow-3" animate={{ y: [0, -12, 0], rotate: [0, 8, 0] }} transition={{ duration: 3.5, repeat: Infinity, delay: 0.5 }}>
          <svg width="38" height="38" viewBox="0 0 40 40" fill="none">
            <path d="M15 20c-3 0-7-2-7-7s4-7 7-7 4 4 4 7-1 7-4 7zm10 0c3 0 7-2 7-7s-4-7-7-7-4 4-4 7 1 7 4 7z" fill="#FFD1DC" opacity="0.4"/>
            <circle cx="20" cy="20" r="3" fill="#FF1493" opacity="0.5"/>
          </svg>
        </motion.div>
      </div>

      {/* Hero Section */}
      <section className="scroll-section hero-section">
        <Section className="hero-content-wrapper">
          <motion.div 
            className="hero-photo-circle"
            animate={{ y: [0, -20, 0], rotate: [0, 5, -5, 0] }}
            transition={{ duration: 6, repeat: Infinity }}
          >
            <img src={coupleImages[0]} alt="Us" />
            <div className="circle-glow"></div>
          </motion.div>

          <div className="hero-text-content">
            <h1 className="main-title">Hey Preksha</h1>
            <p className="main-subtitle">I made a tiny corner of the internet just for you.</p>
            <motion.div 
              className="scroll-indicator"
              animate={{ y: [0, 8, 0], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span>Scroll to explore</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14m0 0l7-7m-7 7l-7-7"/>
              </svg>
            </motion.div>
          </div>
        </Section>
      </section>

      {/* Question 1 - FULL SCREEN PHOTO OVERLAY */}
      <section className="scroll-section q1-section">
        <div className="q1-photo-overlay" style={{ backgroundImage: `url(${coupleImages[0]})` }}>
          <div className="q1-gradient-overlay"></div>
        </div>
        <Section className="q1-content">
          <motion.div
            animate={{ y: [0, -12, 0], rotate: [0, 8, -8, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            <Heart size={75} fill="#FFF" color="#FFF" />
          </motion.div>
          <h2 className="q1-question">Do you know you make my days softer?</h2>
          <div className="q1-buttons">
            <motion.button
              className="q1-btn q1-yes"
              onClick={() => handleButtonClick('q1-yes')}
              whileHover={{ scale: 1.08, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              Yes 🥺
            </motion.button>
            <motion.button
              className="q1-btn q1-obviously"
              onClick={() => handleButtonClick('q1-obviously')}
              whileHover={{ scale: 1.08, y: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              Obviously 🙄
            </motion.button>
          </div>
          {clickedButton === 'q1-yes' || clickedButton === 'q1-obviously' ? (
            <motion.div 
              className="scroll-prompt"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              Scroll down now ↓
            </motion.div>
          ) : null}
        </Section>
        {/* Floating particles */}
        <div className="q1-particles">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="q1-particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -35, 0],
                opacity: [0.3, 0.9, 0.3]
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2
              }}
            />
          ))}
        </div>
      </section>

      {/* Question 2 - WAVE SPLIT DESIGN */}
      <section className="scroll-section q2-section">
        <svg className="q2-wave" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <motion.path
            fill="#9370DB"
            d="M0,160L48,176C96,192,192,224,288,213.3C384,203,480,149,576,154.7C672,160,768,224,864,229.3C960,235,1056,181,1152,154.7C1248,128,1344,128,1392,128L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            animate={{
              d: [
                "M0,160L48,176C96,192,192,224,288,213.3C384,203,480,149,576,154.7C672,160,768,224,864,229.3C960,235,1056,181,1152,154.7C1248,128,1344,128,1392,128L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
                "M0,192L48,197.3C96,203,192,213,288,197.3C384,181,480,139,576,138.7C672,139,768,181,864,186.7C960,192,1056,160,1152,138.7C1248,117,1344,107,1392,101.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z",
                "M0,160L48,176C96,192,192,224,288,213.3C384,203,480,149,576,154.7C672,160,768,224,864,229.3C960,235,1056,181,1152,154.7C1248,128,1344,128,1392,128L1440,128L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
              ]
            }}
            transition={{ duration: 8, repeat: Infinity }}
          />
        </svg>
        <Section className="q2-content">
          <div className="q2-circle-icon">
            <motion.div
              animate={{ rotate: 360, scale: [1, 1.15, 1] }}
              transition={{ rotate: { duration: 10, repeat: Infinity, ease: "linear" }, scale: { duration: 2.5, repeat: Infinity } }}
            >
              <Sparkles size={55} color="#FFF" />
            </motion.div>
          </div>
          <h2 className="q2-question">Do you know I feel safe with you?</h2>
          <div className="q2-buttons">
            <motion.button
              className="q2-btn q2-yes"
              onClick={() => handleButtonClick('q2-yes')}
              whileHover={{ x: 8, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>Yes 🤍</span>
              <Heart size={20} />
            </motion.button>
            <motion.button
              className="q2-btn q2-always"
              onClick={() => handleButtonClick('q2-always')}
              whileHover={{ x: 8, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span>I always have</span>
              <Heart size={20} />
            </motion.button>
          </div>
          {clickedButton === 'q2-yes' || clickedButton === 'q2-always' ? (
            <motion.div 
              className="scroll-prompt scroll-prompt-purple"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              Scroll down now ↓
            </motion.div>
          ) : null}
        </Section>
        {/* Sparkles */}
        <div className="q2-sparkles">
          {[...Array(25)].map((_, i) => (
            <motion.div
              key={i}
              className="q2-sparkle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                scale: [0, 1.5, 0],
                rotate: [0, 180, 360],
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                delay: Math.random() * 3
              }}
            />
          ))}
        </div>
      </section>

      {/* Question 3 - ORBITING STARS */}
      <section className="scroll-section q3-section">
        <div className="q3-orbit-system">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="q3-orbit-star"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
              }}
              animate={{ rotate: 360 }}
              transition={{
                duration: 12 + i * 2,
                repeat: Infinity,
                ease: "linear"
              }}
            >
              <motion.div
                style={{ transform: `translateX(${180 + i * 28}px) translateY(-50%)` }}
                animate={{ scale: [1, 1.5, 1] }}
                transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}
              >
                <Star size={14 + i} fill="#FFD700" color="#FFD700" />
              </motion.div>
            </motion.div>
          ))}
        </div>
        <Section className="q3-content">
          <motion.div
            className="q3-center-star"
            animate={{ 
              scale: [1, 1.25, 1],
              rotate: [0, 180, 360],
              filter: [
                "drop-shadow(0 0 25px rgba(255,215,0,0.6))",
                "drop-shadow(0 0 50px rgba(255,215,0,1))",
                "drop-shadow(0 0 25px rgba(255,215,0,0.6))"
              ]
            }}
            transition={{ duration: 4, repeat: Infinity }}
          >
            <Star size={85} fill="#FFD700" color="#FFD700" />
          </motion.div>
          <h2 className="q3-question">Do you know you're my favourite person?</h2>
          <div className="q3-buttons">
            <motion.button
              className="q3-btn q3-yes"
              onClick={() => handleButtonClick('q3-yes')}
              whileHover={{ scale: 1.12, rotate: 5 }}
              whileTap={{ scale: 0.9 }}
            >
              Yes 💕
            </motion.button>
            <motion.button
              className="q3-btn q3-feb"
              onClick={() => handleButtonClick('q3-feb')}
              whileHover={{ scale: 1.12, rotate: -5 }}
              whileTap={{ scale: 0.9 }}
            >
              I've known it since Feb
            </motion.button>
          </div>
          {clickedButton === 'q3-yes' || clickedButton === 'q3-feb' ? (
            <motion.div 
              className="scroll-prompt scroll-prompt-gold"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              Scroll down now ↓
            </motion.div>
          ) : null}
        </Section>
        {/* Gradient animation */}
        <motion.div
          className="q3-gradient-bg"
          animate={{
            background: [
              "radial-gradient(circle at 20% 50%, rgba(255,182,193,0.3) 0%, transparent 50%)",
              "radial-gradient(circle at 80% 50%, rgba(255,182,193,0.3) 0%, transparent 50%)",
              "radial-gradient(circle at 20% 50%, rgba(255,182,193,0.3) 0%, transparent 50%)"
            ]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </section>

      {/* Playful Hugs */}
      <section className="scroll-section playful-section">
        <Section className="playful-content">
          <motion.div 
            className="playful-image"
            initial={{ x: -80, opacity: 0, rotate: -5 }}
            whileInView={{ x: 0, opacity: 1, rotate: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: false, amount: 0.4 }}
          >
            <img src={coupleImages[1]} alt="Together" />
          </motion.div>
          
          <motion.div 
            className="playful-text-box"
            initial={{ x: 80, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: false, amount: 0.4 }}
          >
            <p className="playful-question-intro">Okay one important question...</p>
            <h2 className="playful-question-title">Do you like endless hugs?</h2>
            <div className="playful-buttons">
              <motion.button
                className="playful-btn playful-yes"
                onClick={() => handleButtonClick('playful-yes')}
                whileHover={{ scale: 1.08, x: 8 }}
                whileTap={{ scale: 0.95 }}
              >
                Yes pls 🫂
              </motion.button>
              <motion.button
                className="playful-btn playful-only-you"
                onClick={() => handleButtonClick('playful-only')}
                whileHover={{ scale: 1.08, x: 8 }}
                whileTap={{ scale: 0.95 }}
              >
                Only from you 😌
              </motion.button>
            </div>
            {clickedButton === 'playful-yes' || clickedButton === 'playful-only' ? (
              <motion.div 
                className="scroll-prompt scroll-prompt-pink"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                Scroll down now ↓
              </motion.div>
            ) : null}
          </motion.div>
        </Section>
      </section>

      {/* Night Sky */}
      <section className="scroll-section night-section">
        <div className="stars-container">
          {[...Array(120)].map((_, i) => (
            <motion.div
              key={i}
              className="star-point"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: Math.random() * 3 + 1,
                height: Math.random() * 3 + 1,
              }}
              animate={{
                opacity: [0.2, 1, 0.2],
                scale: [1, 1.6, 1]
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 3
              }}
            />
          ))}
        </div>

        <Section className="night-content-box">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="night-star-icon"
          >
            <Star size={55} fill="#FFD700" color="#FFD700" />
          </motion.div>
          
          <div className="poem-container">
            <p className="poem-verse">Somehow, with you,</p>
            <p className="poem-verse">everything feels calmer.</p>
            <p className="poem-verse">You feel like home.</p>
            <p className="poem-verse-highlight">Like my night sky.</p>
          </div>
        </Section>
      </section>

      {/* Proposal */}
      <section className="scroll-section proposal-section">
        {/* TOP LEFT - 3 Solid Red Hearts Vertical */}
        <div className="corner-hearts-left">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="solid-heart"
              style={{ top: `${5 + i * 8}%` }}
              animate={{
                y: [0, 5, 0],
              }}
              transition={{
                duration: 2 + i * 0.3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.2
              }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="#FF1493">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            </motion.div>
          ))}
        </div>

        {/* TOP RIGHT - Calendar 14 FEB with sparkle effect */}
        <div className="corner-calendar">
          <motion.div
            className="calendar-icon"
            animate={{
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
              <rect x="10" y="15" width="60" height="55" rx="5" fill="white" stroke="#FF69B4" strokeWidth="2"/>
              <rect x="10" y="15" width="60" height="15" fill="#FF69B4"/>
              <text x="40" y="50" textAnchor="middle" fill="#FF1493" fontSize="24" fontWeight="bold">14</text>
              <text x="40" y="63" textAnchor="middle" fill="#FF69B4" fontSize="10" fontWeight="600">FEB</text>
            </svg>
            {/* White sparkle effect */}
            <motion.div
              className="calendar-sparkle"
              animate={{
                scale: [0.8, 1.3, 0.8],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <div className="sparkle-circle"></div>
            </motion.div>
          </motion.div>
        </div>

        {/* TOP CENTER - Two Cute Rabbits Holding Hands */}
        <motion.div
          className="rabbits-couple"
          animate={{
            y: [0, -10, 0],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <svg width="120" height="80" viewBox="0 0 120 80" fill="none">
            {/* Left Rabbit */}
            <ellipse cx="30" cy="50" rx="18" ry="25" fill="#E0E0E0"/>
            <circle cx="30" cy="40" r="12" fill="#F5F5F5"/>
            <ellipse cx="22" cy="28" rx="4" ry="10" fill="#E0E0E0"/>
            <ellipse cx="38" cy="28" rx="4" ry="10" fill="#E0E0E0"/>
            <circle cx="27" cy="38" r="2" fill="#333"/>
            <circle cx="33" cy="38" r="2" fill="#333"/>
            <circle cx="28" cy="44" r="3" fill="#FFB6C1"/>
            <circle cx="32" cy="44" r="3" fill="#FFB6C1"/>
            <path d="M 27 42 Q 30 44 33 42" stroke="#333" strokeWidth="1" fill="none"/>
            
            {/* Right Rabbit */}
            <ellipse cx="90" cy="50" rx="18" ry="25" fill="#D3D3D3"/>
            <circle cx="90" cy="40" r="12" fill="#E8E8E8"/>
            <ellipse cx="82" cy="28" rx="4" ry="10" fill="#D3D3D3"/>
            <ellipse cx="98" cy="28" rx="4" ry="10" fill="#D3D3D3"/>
            <circle cx="87" cy="38" r="2" fill="#333"/>
            <circle cx="93" cy="38" r="2" fill="#333"/>
            <circle cx="88" cy="44" r="3" fill="#FFB6C1"/>
            <circle cx="92" cy="44" r="3" fill="#FFB6C1"/>
            <path d="M 87 42 Q 90 44 93 42" stroke="#333" strokeWidth="1" fill="none"/>
            
            {/* Holding hands */}
            <line x1="48" y1="55" x2="72" y2="55" stroke="#FFB6C1" strokeWidth="3" strokeLinecap="round"/>
          </svg>
        </motion.div>

        {/* BOTTOM LEFT - Paper Airplane with Dashed Heart Trail */}
        <motion.div
          className="paper-airplane-bottom"
          initial={{ x: '5%', y: '70%' }}
          animate={{
            x: ['5%', '15%', '25%', '15%', '5%'],
            y: ['70%', '60%', '70%', '80%', '70%'],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <svg width="45" height="45" viewBox="0 0 45 45" fill="none">
            <path d="M5 22L40 5L35 30L22 40L18 22L5 22Z" fill="none" stroke="#FF1493" strokeWidth="2.5"/>
            <path d="M18 22L22 40" stroke="#FF1493" strokeWidth="2"/>
          </svg>
        </motion.div>

        {/* Dashed Heart Trail from Airplane */}
        <svg className="dashed-heart-trail" viewBox="0 0 200 200" style={{ position: 'absolute', bottom: '15%', left: '5%', width: '200px', height: '200px', pointerEvents: 'none' }}>
          <motion.path
            d="M 100,30 C 80,10 50,10 30,30 C 10,50 10,80 30,100 L 100,170 L 170,100 C 190,80 190,50 170,30 C 150,10 120,10 100,30 Z"
            stroke="#FF1493"
            strokeWidth="2.5"
            fill="none"
            strokeDasharray="8,8"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.8 }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          />
        </svg>

        <Section className="proposal-box">
          <motion.div
            className="proposal-heart-icon"
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 2.5, repeat: Infinity }}
          >
            <Heart size={90} fill="#FF69B4" color="#FF69B4" />
          </motion.div>
          
          <p className="proposal-hey-text">Preksha,</p>
          <h2 className="proposal-question-text">Will you be my Valentine?</h2>
          
          <div className="proposal-buttons">
            <motion.button
              className="proposal-yes-btn"
              onClick={triggerConfetti}
              whileHover={{ scale: 1.08, y: -3 }}
              whileTap={{ scale: 0.95 }}
              data-testid="valentine-yes-button"
            >
              YES OF COURSE
            </motion.button>
            <motion.button
              className="proposal-no-btn"
              onClick={triggerConfetti}
              whileHover={{ scale: 1.08, y: -3 }}
              whileTap={{ scale: 0.95 }}
              data-testid="valentine-how-could-i-say-no-button"
            >
              How could I say no
            </motion.button>
          </div>
        </Section>
      </section>

      {/* Final Celebration */}
      <section className="scroll-section celebration-section">
        <Section className="celebration-grid">
          <motion.div 
            className="celebration-img img-left"
            initial={{ rotate: -12, scale: 0.85, opacity: 0 }}
            whileInView={{ rotate: -6, scale: 1, opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: false, amount: 0.3 }}
          >
            <img src={coupleImages[0]} alt="Memory" />
          </motion.div>

          <motion.div 
            className="celebration-text-card"
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: false, amount: 0.3 }}
          >
            <p className="celebration-line-1">Valentine's Day is still coming...</p>
            <h2 className="celebration-line-2">But you've been my valentine all along.</h2>
            <p className="celebration-line-3">This is just us.</p>
            <p className="celebration-line-4">I love you, Preksha 💗</p>
            <p className="celebration-sign">- Talin</p>
          </motion.div>

          <motion.div 
            className="celebration-img img-right"
            initial={{ rotate: 12, scale: 0.85, opacity: 0 }}
            whileInView={{ rotate: 6, scale: 1, opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: false, amount: 0.3 }}
          >
            <img src={coupleImages[2]} alt="Memory" />
          </motion.div>
        </Section>
      </section>
    </div>
  );
};

export default App;
